import React from "react"
import ReactDOM from "react-dom"
import Icon from "../Icon"
import { formatPhone, getRandomInt } from "../tools"
import { Web } from "./sip.js"
import CallIntelligencePanel from "../components/call_intelligence/CallIntelligence.jsx"
import { ContactName, getNameShortForm } from "../components/SoftphoneUtil.jsx"
import { startCallTranscription, SetCallID as transcriberSetCallID } from "./transcription.js"
import { Theme, ThemeContext } from "./themes/index.jsx"

const formatTimeMS = _time => {
    let time = _time
    if (isNaN(time) || time < 0) time = 0
    const m = Math.floor(time / 60)
    const s = time % 60
    const result = String(m) + ":" + String(s).padStart(2, "0")
    return result
}

const DID = React.memo(props => {
    // const location = props.location ? `(${props.location})` : ""
    return (
        <div className="stg-softphone-did">
            <div className="stg-softphone-did-title">CALLING AS</div>
            {props.did && <div className="stg-softphone-did-value">{formatPhone(props.did)}</div>}
        </div>
    )
})

const Keyboard = React.memo(props => {
    return (
        <div className="stg-softphone-keyboard">
            <div className="stg-softphone-keyboard-row">
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("1")}>
                    1
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("2")}>
                    2<div>ABC</div>
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("3")}>
                    3<div>DEF</div>
                </div>
            </div>
            <div className="stg-softphone-keyboard-row">
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("4")}>
                    4<div>GHI</div>
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("5")}>
                    5<div>JKL</div>
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("6")}>
                    6<div>NMO</div>
                </div>
            </div>
            <div className="stg-softphone-keyboard-row">
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("7")}>
                    7<div>PQRS</div>
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("8")}>
                    8<div>TUV</div>
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("9")}>
                    9<div>WXYZ</div>
                </div>
            </div>
            <div className="stg-softphone-keyboard-row">
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("*")}>
                    *
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("0")}>
                    0
                </div>
                <div className="stg-softphone-keyboard-button" onClick={() => props.onDigit("#")}>
                    #
                </div>
            </div>
        </div>
    )
})

class DraggableSoftphone extends React.PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            margin: 5,
            dragging: false,
            moved: false,
            hidden: true,
            right: -1000,
        }
    }

    componentDidMount = () => {
        let state = {}
        try {
            state = JSON.parse(window.localStorage.getItem("stg-softphone-position"))
            if (!state) state = {}
        } catch (e) {}

        if (!state.stickX) state.stickX = -5
        if (!state.stickY) state.stickY = -5
        if (!state.bottom || state.bottom < 0) state.bottom = 10
        if (!state.right || state.right < 0) state.right = 10

        window.addEventListener("mousemove", this.handleDrag)
        window.addEventListener("mouseup", this.handleDragEnd)
        window.addEventListener("resize", this.handleResize)

        this.timeout = setTimeout(() => {
            const rect = ReactDOM.findDOMNode(this).getBoundingClientRect()

            state.width = rect.width
            state.height = rect.height

            this.setState(state, this.handleResize)
        }, 100)
    }

    componentWillUnmount = () => {
        if (this.calltimer) {
            clearInterval(this.calltimer)
        }
        if (this.timeout) {
            clearTimeout(this.timeout)
        }
        window.removeEventListener("mousemove", this.handleDrag)
        window.removeEventListener("mouseup", this.handleDragEnd)
        window.removeEventListener("resize", this.handleResize)
    }

    handleDragStart = e => {
        const rect = ReactDOM.findDOMNode(this).getBoundingClientRect()
        this.setState({
            predragging: true,
            dragging: false,
            moved: false,
            dx: rect.right - e.clientX,
            dy: rect.bottom - e.clientY,
            rect: rect,
            oldBottom: this.state.bottom,
            oldRight: this.state.right,
        })
    }

    handleDragEnd = e => {
        this.setState({ predragging: false })
        if (!this.state.dragging) return

        const clientWidth = document.body.clientWidth
        const clientHeight = document.body.clientHeight
        const left = clientWidth - this.state.right - this.state.width
        const top = clientHeight - this.state.bottom - this.state.height

        let stickX = this.state.stickX,
            stickY = this.state.stickY

        const threshold = 30

        if (this.state.right < this.state.oldRight - threshold) {
            stickX = -this.state.right
        } else if (this.state.right > this.state.oldRight + threshold) {
            stickX = left
        }

        if (this.state.bottom < this.state.oldBottom - threshold) {
            stickY = -this.state.bottom
        } else if (this.state.bottom > this.state.oldBottom + threshold) {
            stickY = top
        }

        this.setState({ stickX: stickX, stickY: stickY, dragging: false })

        const position = {
            stickX: this.state.stickX,
            stickY: this.state.stickY,
            bottom: this.state.bottom,
            right: this.state.right,
        }

        window.localStorage.setItem("stg-softphone-position", JSON.stringify(position))
    }

    handleResize = () => {
        const softphoneElement = ReactDOM.findDOMNode(this)
        const rect = softphoneElement.getBoundingClientRect()
        const expandedHeight = 520
        const windowHeight = window.innerHeight

        if (rect.top < expandedHeight) {
            const parentElement = softphoneElement.closest(".stg-softphone-wrapper")
            if (parentElement) {
                const currentBottom = parseInt(parentElement.style.bottom)
                const newBottom = Math.min(currentBottom, windowHeight - expandedHeight - 10)
                parentElement.style.bottom = `${newBottom}px`
            }
        }
    }

    handleDrag = e => {
        if (!this.state.dragging && !this.state.predragging) return

        const clientWidth = window.innerWidth
        const clientHeight = window.innerHeight

        let right = clientWidth - e.clientX - this.state.dx
        let bottom = clientHeight - e.clientY - this.state.dy

        this.setState({ moved: true, dragging: true, predragging: false })
        this.setState(this.fixBoundaries(right, bottom))
    }

    fixBoundaries = (_right, _bottom) => {
        const rect = this.state.rect || ReactDOM.findDOMNode(this).getBoundingClientRect()
        const clientWidth = window.innerWidth
        const clientHeight = window.innerHeight

        let right = _right
        let bottom = _bottom

        if (right < this.state.margin) {
            right = this.state.margin
        }

        if (bottom < this.state.margin) {
            bottom = this.state.margin
        }

        if (right + rect.width > clientWidth - this.state.margin) {
            right = clientWidth - rect.width - this.state.margin
        }

        if (bottom + rect.height > clientHeight - this.state.margin) {
            bottom = clientHeight - rect.height - this.state.margin
        }

        return { right: right, bottom: bottom }
    }

    render() {
        const className = `stg-softphone-wrapper ${this.state.dragging ? " stg-softphone-drag" : ""} ${
            this.props?.options?.darkMode ? "darkMode" : ""
        }`
        return (
            <div className={className} style={{ bottom: this.state.bottom + "px", right: this.state.right + "px" }}>
                {/* style={{ bottom: this.state.bottom + "px", right: this.state.right + "px" }} */}
                <div
                    style={{
                        pointerEvents: this.state.dragging && this.state.moved ? "none" : "unset",
                    }}>
                    <Softphone onDragStart={this.handleDragStart} onResize={this.handleResize} {...this.props} />
                </div>
            </div>
        )
    }
}

class Softphone extends React.PureComponent {
    static contextType = ThemeContext

    constructor(props) {
        super(props)

        this.state = {
            connected: false,
            minimized: !!props?.options?.minimized,
            oncall: false,
            ondialing: false,
            intransfer: false,
            //   call: { phonenumber: "5551231234", direction: "in", status: "DIALING", name: "Test Caller" },
            mute: false,
            paused: false,
            time: "0:00",
            calltimer: 0,
            contact: "",
            digits: props.dial?.number || "",
            providedContact: props.dial?.contact,
            callerid: props.dial?.callerid || "",
            visible: props.visible,
            expandPanel: true,
            enableReconnect: true,
            sipServerId: -1,
            showKeyboard: false,
        }
        window.makeCall = this.makeCall
        this.inputRef = React.createRef()
    }

    makeCall = (number, extra) => {
        if (this.props.onOutgoingCall) {
            console.log("[softphone] calling to " + number)
            const handled = this.props.onOutgoingCall(number, null, extra)
            if (handled === true) {
                this.setState({ waitForIncoming: number })
                return
            }
        }
    }

    connect = () => {
        console.log("[softphone] connecting")
        this.simpleUser
            .connect()
            .then(() => {
                console.log("[softphone] registering")
                return this.simpleUser.register()
            })
            .catch(e => {
                console.log("[softphone] error ", e)
                this.props.eventHandler.onError()
                this.reconnect()
            })
    }

    reconnect = () => {
        this.simpleUser.disconnect()
        if (this.state.enableReconnect) {
            setTimeout(this.createSip, 2000)
        }
    }

    createSip = async () => {
        console.log("[softphone] creating SIP user agent")
        const audio = document.createElement("audio")
        document.body.appendChild(audio)
        this.callAudio = audio

        const credentials = this.props.credentials

        const aor = "sip:" + credentials.ext + "@" + credentials.server
        const iceServerId = getRandomInt(this.props.iceServers.length)
        const iceServer = this.props.iceServers[iceServerId]

        let sipServerId =
            this.state.sipServerId < 0 ? getRandomInt(this.props.sipServers.length) : this.state.sipServerId + 1
        if (sipServerId >= this.props.sipServers.length) {
            sipServerId = 0
        }
        const sipServer = this.props.sipServers[sipServerId]

        const options = {
            aor,
            reconnectionAttempts: 0,
            userAgentOptions: {
                authorizationUsername: credentials.ext,
                authorizationPassword: credentials.pwd,
                sessionDescriptionHandlerFactoryOptions: {
                    peerConnectionConfiguration: {
                        iceServers: [iceServer],
                        iceTransportPolicy: "relay",
                    },
                },
            },
            media: {
                constraints: {
                    audio: {
                        autoGainControl: false,
                        googAutoGainControl: false,
                        noiseSuppression: false,
                    },
                    video: false,
                },
                remote: {
                    audio: audio,
                    video: false,
                },
            },
        }

        this.simpleUser = new Web.SimpleUser("wss://" + sipServer + ":4443", options)
        this.simpleUser.delegate = {
            onRegistered: () => {
                console.log("[softphone] registered")
                this.setState({ connected: true })
                this.props.onSoftphoneRegister(true)
                this.props.eventHandler.onRegistered()
            },
            onCallCreated: () => {
                console.log(this.props)
                if (this.props?.transcriptionEnabled) {
                    console.log("[softphone] transcription started")
                    startCallTranscription(this.simpleUser.session)
                } else {
                    console.log("[softphone] transcribing disabled")
                }
            },
            onServerDisconnect: () => {
                console.log("[softphone] disconnected")
                this.setState({ connected: false })
                this.reconnect()
                this.props.eventHandler.onServerDisconnect()
            },
            onUnregistered: () => {
                console.log("[softphone] unregistered")
                this.props.eventHandler.onUnregistered()
            },
            onCallHangup: () => {
                console.log("[softphone] call finished")
                if (this.props.onHangup) this.props.onHangup()
                this.setState({
                    oncall: false,
                    ondialing: false,
                    intransfer: false,
                    calltimer: 0,
                    time: "0:00",
                })

                if (this.props.qualityMonitor) {
                    this.props.qualityMonitor(null)
                }
                this.props.eventHandler.onCallHangup()
            },
            onCallAnswered: () => {
                console.log("[softphone] call answered")
                this.setState({
                    oncall: true,
                    ondialing: false,
                    intransfer: false,
                    //calltimer: 0,
                    //time: "0:00",
                })

                if (this.props.qualityMonitor) {
                    this.props.qualityMonitor(this.simpleUser.session)
                }
                this.props.eventHandler.onCallAnswered()
            },
            onCallReceived: session => {
                console.log("[softphone] call received")
                let number = "(unknown)"
                let name = ""
                try {
                    number = this.simpleUser.session.incomingInviteRequest.message.from.uri.normal.user
                    name = this.simpleUser.session.incomingInviteRequest.message.from._displayName || ""
                } catch (e) {
                    console.log(e)
                }

                if (this.props.onIncomingCall) this.props.onIncomingCall(number, name)

                if (number?.startsWith("+1")) number = number.substring(2, number.length)

                const wait = this.state.waitForIncoming

                this.setState(
                    {
                        ondialing: true,
                        visible: true,
                        call: {
                            phonenumber: number,
                            direction: "in",
                            status: "DIALING",
                            name: name,
                        },
                    },
                    () => {
                        if (wait || !!this.props.autoAnswer) {
                            console.log("[softphone] anwering waited call")
                            this.setState({ waitForIncoming: false })
                            this.handleAnswer()
                            this.props.eventHandler.onCallReceived()
                        }
                    }
                )
            },
        }

        this.setState({ sipServerId: sipServerId }, this.connect)
    }

    componentDidMount = () => {
        const audio = document.createElement("audio")
        document.body.appendChild(audio)
        audio.src = "https://app.batchdialer.com/audio/beep.mp3"
        this.beep = audio
        if (this.props.connection) {
            console.log("[softphone] agent websocket is connected, creating SIP user agent")
            this.createSip()
        }
        this.audio = audio
    }

    componentWillUnmount = () => {
        console.log("[softphone] component unmount, disconnecting SIP")
        if (this.audio) {
            this.audio.remove()
        }
        if (this.callAudio) {
            this.callAudio.remove()
        }
        if (this.simpleUser) {
            this.simpleUser.unregister().then(() => {
                this.simpleUser.disconnect()
            })
        }
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (this.props.connection && !this.state.connected && prevState.connected) {
            if (!!this.simpleUser && !!window.scheduleReload) {
                window.scheduleReload()
            }
        }

        if (this.props.connection !== prevProps.connection) {
            console.log(
                "[softphone] agent socket connection state changed ",
                prevProps.connection,
                " => ",
                this.props.connection
            )

            if (this.props.connection && !this.state.connected && !this.simpleUser) {
                setTimeout(() => {
                    this.createSip()
                }, 1000)
            }

            /*
            if (this.props.connection && !this.state.connected) {
                if (!!this.simpleUser) {
                    console.log("[softphone] force disconnecting")
                    this.simpleUser.disconnect()
                }
                setTimeout(() => {
                    this.createSip()
                }, 1000)
            }
            if (!this.props.connection && this.state.connected) {
                this.setState({ connected: false }, () => {
                    console.log("[softphone] force disconnecting SIP")
                    if (this.simpleUser) {
                        this.simpleUser.disconnect()
                    }
                })
            }
            */
        }
        if (this.props.visible !== prevProps.visible) {
            this.setState({ visible: this.props.visible })
        }
        if (prevState.minimized !== this.state.minimized) {
            this.props.onResize()
        }
        if (!prevState.oncall && this.state.oncall) {
            this.setState({ digits: "", calltimer: 0, time: "0:00" }, () => {
                this.calltimer = setInterval(() => {
                    const calltimer = this.state.calltimer + 1
                    this.setState({
                        calltimer: calltimer,
                        time: formatTimeMS(calltimer),
                    })
                }, 1000)
            })
        }
        if (prevState.oncall && !this.state.oncall) {
            this.setState({ digits: "" })
            clearInterval(this.calltimer)
        }

        if (this.props.contact && prevProps.contact !== this.props.contact) {
            this.setState({
                contact: this.props.contact?.name || "",
                digits: this.props.contact?.phonenumber || "",
            })
        }

        if (this.props.dial && prevProps.dial !== this.props.dial) {
            this.setState(
                {
                    contact: this.props.contact?.name || "",
                    digits: this.props.dial?.number || "",
                    providedContact: this.props.dial?.contact,
                    callerid: this.props.dial?.callerid || "",
                },
                () => {
                    if (this.state.digits) {
                        this.handleCall() //call direct number when dial in
                    }
                }
            )
        }

        if (prevProps.call !== this.props.call) {
            const call = this.props.call
            //let duration = Math.round((new Date().getTime() - Date.parse(call.calldate)) / 1000)
            this.setState({
                //calltimer: duration,
                //time: formatTimeMS(duration),
                oncall: call.status === "ANSWERED",
                ondialing: call.status === "DIALING" && call.direction === "in",
            })
            transcriberSetCallID(call?.id)
        }

        if (this.state.connected !== prevState.connected && !!this.props.onChangeState) {
            this.props.onChangeState(this.state.connected)
        }
    }

    handleClose = () => {
        if (!!this.props?.options?.hangupOnClose) {
            this.handleHangup()
        }
        this.props.onHide()
        this.props.eventHandler.onHide()
    }

    handleMinimize = () => {
        this.setState({ minimized: true })
    }

    handleToggleKeyboard = () => {
        if (this.state.paused) return
        this.setState({ showKeyboard: !this.state.showKeyboard })
        if (this.state.minimized) {
            this.setState({ minimized: false })
        }
    }
    handleExpandPanel = () => {
        this.setState({ minimized: !this.state.minimized })
    }

    pause = async delay => new Promise(resolve => setTimeout(resolve, delay))

    sendDTMFString = async str => {
        for (var i = 0; i < str.length; i++) {
            const char = str.charAt(i)
            await this.simpleUser.sendDTMF(char)
        }
    }

    handleToggleMute = () => {
        if (this.state.mute) {
            this.simpleUser.unmute()
        } else {
            this.simpleUser.mute()
        }
        this.setState({ mute: !this.state.mute })
    }

    handleTogglePause = () => {
        if (this.state.paused) {
            this.simpleUser.unhold()
        } else {
            this.simpleUser.hold()
        }
        this.setState({ paused: !this.state.paused })
    }

    handleDigit = digit => {
        this.setState({ digits: this.state.digits + digit })
        if (this.state.oncall) {
            this.simpleUser.sendDTMF(digit)
        }
    }

    handleTransfer = () => {
        this.sendDTMFString("*3")
        this.setState({ intransfer: false })
    }

    handleBackToClient = () => {
        this.sendDTMFString("*0")
        this.setState({ intransfer: false })
    }

    handleConference = () => {
        this.sendDTMFString("*4")
        this.setState({ intransfer: false })
    }

    handleBackspace = () => {
        this.setState({
            digits: this.state.digits.substr(0, this.state.digits.length - 1),
        })
    }

    preventDefault = e => e.preventDefault()

    handleCall = () => {
        let number = this.state.digits
        let contact = this.state.providedContact
        let callerid = this.state.callerid

        if (number.length === 11 && number.substring(0, 1) === "1") {
            number = number.substring(1)
        }
        if (number === "") return
        this.setState({
            oncall: true,
            ondialing: true,
            intransfer: false,
            call: {
                phonenumber: number,
                direction: "out",
                status: "CALLING",
                name: "",
            },
        })

        if (this.props.onOutgoingCall) {
            console.log("[softphone] calling to " + number + " via API")

            const handled = this.props.onOutgoingCall(contact ? contact : number, callerid)
            if (handled === true) {
                this.setState({ waitForIncoming: number })
                return
            }
        }
        this.setState({ waitForIncoming: false })

        console.log("[softphone] calling to " + number + " via SIP", this.props.credentials)
        this.simpleUser.call("sip:" + number + "@" + this.props.credentials.server, {
            extraHeaders: this.props.headers,
        })
    }

    handleAnswer = e => {
        if (!!this.props.autoAnswer && this.state.call.direction === "in") {
            console.log("[softphone] beeeeep")
            this.beep.play()
        }
        this.setState({
            ondialing: false,
            //calltimer: 0,
        })
        console.log("[softphone] answering call", e ? " using the green button" : "automatically")
        this.simpleUser.answer()
    }

    handleHangup = () => {
        if (!this.state.waitForIncoming) {
            if (this.state.ondialing && this.state.call && this.state.call.direction === "in") {
                console.log("[softphone] hanging up")
                this.simpleUser.decline().catch(e => console.log(e))
            } else {
                if (this.props.onDoHangup) {
                    console.log("[softphone] hanging up via API")
                    this.props.onDoHangup()
                } else {
                    console.log("[softphone] hanging up via SIP")
                    this.simpleUser.hangup().catch(e => console.log(e))
                }
            }
        } else {
            if (this.props.onDoHangup) {
                console.log("[softphone] hanging up via API")
                this.props.onDoHangup()
            }
        }
        this.setState({
            waitForIncoming: false,
            oncall: false,
            ondialing: false,
            call: {},
        })
    }

    formatPhone = number => {
        if (number.length != 10) return number
        return (number.substr(0, 3) + " " + number.substr(3, 3) + " " + number.substr(6)).trim()
    }
    reverseString = s => {
        return s.split("").reverse().join("")
    }

    handleEditNumber = e => {
        this.setState({ digits: e.target.value.replace(/\D/g, "") })
    }

    handleEditClick = () => {
        this.inputRef.current.focus()
    }
    handleTogglePanel = () => {
        this.setState({ expandPanel: !this.state.expandPanel })
    }

    getContactInfo = () => {
        const name = this.state.contact?.toLowerCase()
        if (!name || name.includes("unknown")) {
            return this.state.call?.phonenumber
        }
        return this.state.contact
    }

    render() {
        if (!this.state.connected) return ""

        if (this.state.ondialing && this.state.call && this.state.call.direction === "in")
            return (
                <div className={`stg-softphone`} onMouseDown={this.props.onDragStart} onDragStart={this.preventDefault}>
                    {!this.props.autoAnswer && <audio src={this.props.ringTone} autoPlay loop />}
                    <div className="stg-softphone-left">
                        <span className="softphone-close-icon">
                            <span className="cursor_pointer" onClick={this.handleClose}>
                                <Icon icon="close" />
                            </span>
                        </span>
                        {!this.state.minimized && (
                            <>
                                <div className="stg-softphone-number">
                                    <div className="stg-softphone-number-digits-incoming">
                                        {this.state.providedContact && (
                                            <ContactName
                                                contact={this.state.providedContact}
                                                onClick={this.props.eventHandler?.onClickContactOpen}
                                            />
                                        )}
                                        {this.state.call?.phonenumber
                                            ? this.formatPhone(this.state.call?.phonenumber)
                                            : this.state.call?.clid
                                            ? this.formatPhone(this.state.call?.clid)
                                            : ""}
                                    </div>
                                </div>
                                <div className="stg-softphone-lead">{this.getContactInfo()}</div>

                                <div className="stg-softphone-callbutton-incoming-row">
                                    <div className="stg-softphone-callbutton">
                                        <img
                                            data-inline-svg
                                            src={this.context.images.IconPhoneGreen}
                                            alt=""
                                            onClick={this.handleAnswer}
                                        />
                                    </div>
                                    <div className="stg-softphone-callbutton">
                                        <img
                                            data-inline-svg
                                            src={this.context.images.IconPhoneRed}
                                            alt=""
                                            onClick={this.handleHangup}
                                        />
                                    </div>
                                    <div></div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )

        if (!this.state.visible)
            return (
                <>
                    {this.state.ondialing && (
                        <audio src="https://app.batchdialer.com/audio/outgoing.mp3" autoPlay loop />
                    )}
                </>
            )

        return (
            <div
                className={`stg-softphone softphone-ai ${this.state.minimized ? "minimized" : ""}`}
                onMouseDown={this.props.onDragStart}
                onDragStart={this.preventDefault}>
                <CallIntelligencePanel
                    contact={this.state?.contact || this.state?.providedContact}
                    call={this.props.call}
                    minimized={this.state.minimized}
                    eventHandler={this.props.eventHandler}
                    session={this.simpleUser.session}
                />
                {this.state.ondialing && <audio src="https://app.batchdialer.com/audio/outgoing.mp3" autoPlay loop />}
                <div className={`stg-softphone-left`}>
                    {/* right corner close button */}
                    <span className="softphone-close-icon">
                        <span
                            className="cursor_pointer"
                            onClick={this.handleClose}
                            onMouseDown={e => e.stopPropagation()}>
                            <Icon icon="close" />
                        </span>
                    </span>

                    {/* outbound number */}
                    {this.state.oncall && !this.state.minimized && (
                        <div className="stg-softphone-header">
                            {this.state.ondialing ? (
                                "Connecting..."
                            ) : (
                                <DID
                                    did={this.props.call && this.props.call.did}
                                    location={this.props.call && this.props.call.didlocation}
                                />
                            )}
                        </div>
                    )}

                    {/* number input */}
                    {!this.state.minimized && (
                        <>
                            {!this.state.oncall && (
                                <div className="stg-softphone-number">
                                    <input
                                        ref={this.inputRef}
                                        type="text"
                                        onChange={this.handleEditNumber}
                                        style={{
                                            position: "absolute",
                                            zIndex: "-1",
                                        }}
                                        value={this.state.digits}
                                    />
                                    <div className="stg-softphone-number-digits" onClick={this.handleEditClick}>
                                        {(this.state.digits &&
                                            this.reverseString(this.formatPhone(this.state.digits))) || (
                                            <span className="stg-softphone-number-digits-placeholder">
                                                Enter Phone Number
                                            </span>
                                        )}
                                    </div>
                                    <Icon icon="backspace" onClick={this.handleBackspace} />
                                    <div className="stg-softphone-callbutton">
                                        {this.state.oncall ? (
                                            <img
                                                data-inline-svg
                                                src={this.context.images.IconPhoneRed}
                                                alt=""
                                                onClick={this.handleHangup}
                                                onMouseDown={e => e.stopPropagation()}
                                            />
                                        ) : (
                                            <img
                                                data-inline-svg
                                                src={this.context.images.IconPhoneGreen}
                                                alt=""
                                                onClick={this.handleCall}
                                                onMouseDown={e => e.stopPropagation()}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            {!this.state.oncall && <Keyboard onDigit={this.handleDigit} />}
                        </>
                    )}

                    {/* floating keyboard */}
                    {this.state.oncall && !this.state.minimized && !this.state.paused && (
                        <>
                            <div
                                className={`stg-softphone-floating-keyboard-back ${
                                    this.state.showKeyboard ? "active" : ""
                                }`}></div>
                            <div
                                className={`stg-softphone-floating-keyboard ${
                                    this.state.showKeyboard ? "active" : ""
                                }`}>
                                <header className="stg-softphone-floating-keyboard-header">
                                    <div
                                        className="stg-softphone-floating-keyboard-hide"
                                        onClick={this.handleToggleKeyboard}
                                        onMouseDown={e => e.stopPropagation()}>
                                        <Icon icon="chevron-down" />
                                        <span>Hide Keypad</span>
                                    </div>
                                </header>
                                <main>
                                    <div className="stg-softphone-incall-number">
                                        <div className="stg-softphone-incall-digits">
                                            {this.reverseString(this.formatPhone(this.state.digits))}
                                        </div>
                                        <Icon icon="backspace" onClick={this.handleBackspace} />
                                    </div>
                                    <Keyboard onDigit={this.handleDigit} />
                                </main>
                            </div>
                        </>
                    )}

                    {/* minimized contact info */}
                    {this.state.oncall && this.state.minimized && (
                        <div className="stg-softphone-minimized">
                            <div className="stg-softphone-contact">
                                {this.state.providedContact && (
                                    <ContactName
                                        contact={this.state.providedContact}
                                        onClick={this.props.eventHandler?.onClickContactOpen}
                                    />
                                )}
                                {this.state.call?.phonenumber
                                    ? formatPhone(this.state.call?.phonenumber)
                                    : this.state.call?.clid
                                    ? formatPhone(this.state.call?.clid)
                                    : ""}
                            </div>

                            <div className="stg-softphone-callbutton stg-softphone-text-right">
                                {this.state.oncall ? (
                                    <div
                                        className="stg-softphone-hangup"
                                        onClick={this.handleHangup}
                                        onMouseDown={e => e.stopPropagation()}>
                                        <img data-inline-svg src={this.context.images.IconHangup} alt="" />
                                    </div>
                                ) : (
                                    <img
                                        data-inline-svg
                                        src={this.context.images.IconPhoneGreen}
                                        alt=""
                                        onClick={this.handleCall}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* maximized caller */}
                    {this.state.oncall && !this.state.minimized && (
                        <div className="stg-softphone-caller">
                            <div className="stg-softphone-caller-avatar">
                                {getNameShortForm(this.state.providedContact || this.state.contact)}
                            </div>
                            {this.state.providedContact && (
                                <div className="stg-softphone-contact">
                                    <ContactName
                                        contact={this.state.providedContact}
                                        onClick={this.props.eventHandler?.onClickContactOpen}
                                    />
                                    {formatPhone(
                                        this.state.call?.phonenumber
                                            ? this.state.call?.phonenumber
                                            : this.state.call?.clid
                                            ? this.state.call?.clid
                                            : ""
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* timer */}
                    {this.state.oncall && (
                        <div className="stg-softphone-time-minimized">
                            {this.state.ondialing ? "Connecting..." : this.state.time}
                        </div>
                    )}

                    {/* controls */}
                    {this.state.oncall && !this.state.intransfer && (
                        <>
                            <div className="stg-softphone-callbuttons">
                                <div
                                    title={this.state.mute ? "Unmute" : "Mute"}
                                    className={`stg-softphone-callbuttons-button ${
                                        this.state.ondialing ? "disabled" : this.state.mute ? "active" : ""
                                    }`}
                                    onClick={this.handleToggleMute}>
                                    <div onMouseDown={e => e.stopPropagation()}>
                                        <img data-inline-svg src={this.context.images.BtnMute} alt="" />{" "}
                                    </div>
                                    Mute
                                </div>
                                <div
                                    title={this.state.paused ? "Unhold" : "Hold"}
                                    className={`stg-softphone-callbuttons-button ${
                                        this.state.ondialing ? "disabled" : this.state.paused ? "active" : ""
                                    }`}
                                    onClick={this.handleTogglePause}>
                                    <div onMouseDown={e => e.stopPropagation()}>
                                        <img data-inline-svg src={this.context.images.BtnPause} alt="" />
                                    </div>
                                    Hold
                                </div>
                                <div
                                    title={!this.state.showKeyboard ? "Show Keyboard" : "Hide Keyboard"}
                                    className={`stg-softphone-callbuttons-button ${
                                        this.state.ondialing || this.state.paused
                                            ? "disabled"
                                            : this.state.showKeyboard
                                            ? "active"
                                            : ""
                                    }`}
                                    onClick={this.handleToggleKeyboard}>
                                    <div onMouseDown={e => e.stopPropagation()}>
                                        <img data-inline-svg src={this.context.images.BtnKeyboard} alt="" />
                                    </div>
                                    Keypad
                                </div>
                            </div>

                            {/* hangup */}
                            {!this.state.minimized && (
                                <div
                                    className="stg-softphone-hangup"
                                    onClick={this.handleHangup}
                                    onMouseDown={e => e.stopPropagation()}>
                                    <img data-inline-svg src={this.context.images.IconHangup} alt="" />
                                    End Call
                                </div>
                            )}

                            <div
                                className="stg-softphone-expand-panel"
                                onClick={this.handleExpandPanel}
                                onMouseDown={e => e.stopPropagation()}>
                                <Icon
                                    style={{ fontSize: "20px" }}
                                    icon={this.state.minimized ? "angle-down" : "angle-up"}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        )
    }
}

export default Theme(DraggableSoftphone)
